import { InfoCircleOutlined } from '@ant-design/icons'
import { Form, Popover } from 'antd'
import type { FormInstance } from 'antd/es/form'
import type { NamePath } from 'antd/es/form/interface'
import type { RUGFieldSource } from 'pages/reporting/reports/rug/rugReportData'
import React from 'react'
import styled from 'styled-components'

type FieldValue = string | number | boolean | object | null | undefined

const SourceButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.forecast.secondaryText};
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

const SourceContent = styled.div`
  max-width: 16rem;
`

const SourceTitle = styled.div`
  margin-bottom: 0.25rem;
  font-weight: 600;
`

const sourceLabels: Record<RUGFieldSource, string> = {
  inspectionForm: 'Inspection form',
  salesforce: 'Salesforce',
  linkedJobData: 'Linked job data',
}

const sourceDescriptions: Record<RUGFieldSource, string> = {
  inspectionForm:
    'This value was pre-populated from the linked RUG inspection form.',
  salesforce:
    'This value was pre-populated from Salesforce data linked to the job.',
  linkedJobData:
    'This value was pre-populated from linked unit, site, job, or asset data.',
}

const valuesMatch = (left: FieldValue, right: FieldValue): boolean => {
  if (left === right) return true
  if (left === undefined || right === undefined) return false

  try {
    return JSON.stringify(left) === JSON.stringify(right)
  } catch {
    return false
  }
}

const hasFieldValue = (value: FieldValue): boolean => {
  if (value === undefined || value === null) return false
  if (typeof value === 'string') return value.trim().length > 0
  return !Array.isArray(value) || value.length > 0
}

interface SourceIndicatorProps {
  form: FormInstance
  name: NamePath
  source: RUGFieldSource | undefined
  initialValue: FieldValue
}

export const SourceIndicator: React.FC<SourceIndicatorProps> = ({
  form,
  name,
  source,
  initialValue,
}) => {
  const currentValue = Form.useWatch<FieldValue>(name, form)
  const [baselineValue, setBaselineValue] = React.useState(initialValue)

  React.useEffect(() => {
    if (!hasFieldValue(baselineValue) && hasFieldValue(currentValue)) {
      setBaselineValue(currentValue)
    }
  }, [baselineValue, currentValue])

  if (
    !source ||
    !hasFieldValue(currentValue) ||
    !valuesMatch(currentValue, baselineValue)
  ) {
    return null
  }

  return (
    <Popover
      trigger='hover'
      content={
        <SourceContent>
          <SourceTitle>{sourceLabels[source]}</SourceTitle>
          <div>{sourceDescriptions[source]}</div>
        </SourceContent>
      }>
      <SourceButton
        type='button'
        aria-label={`Show source for this field: ${sourceLabels[source]}`}>
        <InfoCircleOutlined aria-hidden='true' />
      </SourceButton>
    </Popover>
  )
}
